import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { LeadActivityKind, LeadActivityOutcome, LeadDetailRecord, LeadStatus } from '@shared/types';
import { subscribeToShellDeleteRequests } from '@renderer/app/events/shell-delete-events';
import { useShellStore } from '@renderer/app/state/shell-store';
import { EditorBodyLayout, EditorHeroCard, EditorPageLayout, EditorSectionCard } from '@renderer/shared/components/editor-scaffold';
import { Icon, normalizeBusinessIconName } from '@renderer/shared/components/icons';
import {
  EditorTabs,
  EmptyState,
  OverflowMenuButton,
  SearchableSelectField,
  SelectField,
  StatusBadge,
  TextAreaField,
  TextField,
  ValuePair
} from '@renderer/shared/components/primitives';
import { useRegisterEditorSaveHandler } from '@renderer/shared/hooks/use-register-editor-save-handler';
import { useI18n } from '@renderer/shared/i18n';
import { areDraftsEqual } from '@renderer/shared/utils/draft-equality';
import { openExternal, toTelUrl } from '@renderer/shared/utils/open-external';
import { LeadStatusControl, leadStatusOrder } from './lead-status-control';
import { convertLead, deleteLeads, getLeadById, logLeadActivity, updateLead } from './leads-repository';
import { selectLeadEditorModel } from './leads-selectors';
import { useLeadsViewStore } from './leads-view-store';

interface LeadDraft {
  name: string;
  companyName: string;
  contactName: string;
  phone: string;
  email: string;
  currentWebsiteUrl: string;
  city: string;
  source: string;
  customerCategoryId: string;
  status: LeadStatus;
  nextContactAt: string;
  lostReason: string;
  tags: string;
}

interface ActivityDraft {
  kind: LeadActivityKind;
  outcome: LeadActivityOutcome;
  comment: string;
  nextContactAt: string;
  status: '' | LeadStatus;
}

interface ConversionDraft {
  customerId: string;
  customerName: string;
  projectName: string;
  primaryDomain: string;
}

const leadActivityKinds: LeadActivityKind[] = ['call', 'email', 'whatsapp', 'meeting', 'note', 'status_change'];
const leadActivityOutcomes: LeadActivityOutcome[] = [
  'reached',
  'no_answer',
  'left_message',
  'interested',
  'not_interested',
  'scheduled_follow_up',
  'qualified',
  'converted',
  'lost',
  'note'
];

function formatDateInputValue(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function toIsoDate(value: string): string | null {
  return value ? `${value}T12:00:00.000Z` : null;
}

function toDraft(detail: LeadDetailRecord): LeadDraft {
  return {
    name: detail.lead.name,
    companyName: detail.lead.companyName ?? '',
    contactName: detail.lead.contactName ?? '',
    phone: detail.lead.phone ?? '',
    email: detail.lead.email ?? '',
    currentWebsiteUrl: detail.lead.currentWebsiteUrl ?? '',
    city: detail.lead.city ?? '',
    source: detail.lead.source ?? '',
    customerCategoryId: detail.lead.customerCategoryId ?? '',
    status: detail.lead.status,
    nextContactAt: formatDateInputValue(detail.lead.nextContactAt),
    lostReason: detail.lead.lostReason ?? '',
    tags: detail.lead.tags.join(', ')
  };
}

function createActivityDraft(): ActivityDraft {
  return {
    kind: 'call',
    outcome: 'reached',
    comment: '',
    nextContactAt: '',
    status: ''
  };
}

function toConversionDraft(detail: LeadDetailRecord): ConversionDraft {
  const customerName = detail.lead.companyName || detail.lead.name;
  return {
    customerId: detail.lead.customerId ?? '',
    customerName,
    projectName: `${customerName} Website`,
    primaryDomain: detail.lead.currentWebsiteUrl ?? ''
  };
}

function toTags(value: string): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function LeadEditorScreen(): JSX.Element {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const { locale, t } = useI18n();
  const setRouteDetailTitle = useShellStore((state) => state.setRouteDetailTitle);
  const { editorTab, setEditorTab } = useLeadsViewStore();
  const [detail, setDetail] = useState<LeadDetailRecord | null>(null);
  const [draft, setDraft] = useState<LeadDraft | null>(null);
  const [activityDraft, setActivityDraft] = useState<ActivityDraft>(createActivityDraft);
  const [conversionDraft, setConversionDraft] = useState<ConversionDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [conversionError, setConversionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const savedDraft = useMemo(() => (detail ? toDraft(detail) : null), [detail]);

  useEffect(() => {
    if (!leadId) {
      setError('Missing lead id.');
      return;
    }

    let active = true;
    void getLeadById(leadId)
      .then((nextDetail) => {
        if (!active) {
          return;
        }

        if (!nextDetail) {
          setError('Lead not found.');
          return;
        }

        setDetail(nextDetail);
        setDraft(toDraft(nextDetail));
        setConversionDraft(toConversionDraft(nextDetail));
        setRouteDetailTitle(nextDetail.lead.name);
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Lead unavailable.');
        }
      });

    return () => {
      active = false;
      setRouteDetailTitle(null);
    };
  }, [leadId, setRouteDetailTitle]);

  useEffect(
    () =>
      subscribeToShellDeleteRequests((detailRequest) => {
        if (detailRequest.domain !== 'leads' || !detail || !detailRequest.ids.includes(detail.lead.id)) {
          return;
        }

        void deleteLeads({ ids: [detail.lead.id] }).then(() => navigate('/leads'));
      }),
    [detail, navigate]
  );

  useRegisterEditorSaveHandler(
    detail && draft && savedDraft
      ? () => {
          if (isSaving) {
            return;
          }

          const nextIsDirty = !areDraftsEqual(draft, savedDraft);
          if (!nextIsDirty) {
            return;
          }

          void handleSave();
        }
      : null
  );

  if (error) {
    return (
      <div className="av-page">
        <EmptyState title={t('leads.title')} description={error} />
      </div>
    );
  }

  if (!detail || !draft || !conversionDraft) {
    return <div className="av-page" />;
  }

  const activeDetail = detail;
  const activeDraft = draft;
  const activeConversionDraft = conversionDraft;
  const model = selectLeadEditorModel(activeDetail, { locale, t });
  const isDirty = savedDraft ? !areDraftsEqual(activeDraft, savedDraft) : false;
  const isConverted = activeDetail.lead.status === 'converted' || Boolean(activeDetail.lead.convertedAt);
  const statusOptions = leadStatusOrder.map((value) => ({ value, label: t(`enums.leadStatus.${value}`) }));
  const activityKindOptions = leadActivityKinds.map((value) => ({ value, label: t(`enums.leadActivityKind.${value}`) }));
  const activityOutcomeOptions = leadActivityOutcomes.map((value) => ({ value, label: t(`enums.leadActivityOutcome.${value}`) }));
  const customerOptions = [
    { value: '', label: t('leads.conversion.newCustomer') },
    ...activeDetail.customerSummaries.map((customer) => ({ value: customer.id, label: customer.name }))
  ];
  const customerCategoryOptions = [
    { value: '', label: t('leads.filters.unassignedCategory'), icon: 'times' as const },
    ...activeDetail.customerCategories
      .filter((category) => !category.archivedAt || category.id === activeDraft.customerCategoryId)
      .map((category) => ({
        value: category.id,
        label: category.name,
        icon: normalizeBusinessIconName(category.iconName),
        description: category.archivedAt ? t('common.archived') : undefined
      }))
  ];

  async function handleDeleteLead(): Promise<void> {
    if (!window.confirm(t('common.confirmDeleteSoft'))) {
      return;
    }

    await deleteLeads({ ids: [activeDetail.lead.id] });
    navigate('/leads');
  }

  async function handleSave(): Promise<void> {
    setIsSaving(true);
    setSaveError(null);

    try {
      const nextDetail = await updateLead({
        id: activeDetail.lead.id,
        name: activeDraft.name.trim(),
        companyName: activeDraft.companyName.trim() ? activeDraft.companyName.trim() : null,
        contactName: activeDraft.contactName.trim() ? activeDraft.contactName.trim() : null,
        phone: activeDraft.phone.trim() ? activeDraft.phone.trim() : null,
        email: activeDraft.email.trim() ? activeDraft.email.trim() : null,
        currentWebsiteUrl: activeDraft.currentWebsiteUrl.trim() ? activeDraft.currentWebsiteUrl.trim() : null,
        city: activeDraft.city.trim() ? activeDraft.city.trim() : null,
        source: activeDraft.source.trim() ? activeDraft.source.trim() : null,
        customerCategoryId: activeDraft.customerCategoryId || null,
        status: activeDraft.status,
        nextContactAt: toIsoDate(activeDraft.nextContactAt),
        lostReason: activeDraft.lostReason.trim() ? activeDraft.lostReason.trim() : null,
        tags: toTags(activeDraft.tags)
      });
      setDetail(nextDetail);
      setDraft(toDraft(nextDetail));
      setConversionDraft(toConversionDraft(nextDetail));
      setRouteDetailTitle(nextDetail.lead.name);
    } catch (nextError) {
      setSaveError(nextError instanceof Error ? nextError.message : 'Lead save failed.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel(): void {
    setSaveError(null);
    setDraft(toDraft(activeDetail));
  }

  async function handleLogActivity(): Promise<void> {
    setActivityError(null);

    try {
      const nextDetail = await logLeadActivity({
        leadId: activeDetail.lead.id,
        kind: activityDraft.kind,
        outcome: activityDraft.outcome,
        comment: activityDraft.comment.trim() ? activityDraft.comment.trim() : null,
        nextContactAt: toIsoDate(activityDraft.nextContactAt),
        status: activityDraft.status || undefined
      });
      setDetail(nextDetail);
      setDraft(toDraft(nextDetail));
      setConversionDraft(toConversionDraft(nextDetail));
      setActivityDraft(createActivityDraft());
    } catch (nextError) {
      setActivityError(nextError instanceof Error ? nextError.message : 'Lead activity failed.');
    }
  }

  async function handleConvertLead(): Promise<void> {
    setConversionError(null);
    setIsConverting(true);

    try {
      const result = await convertLead({
        leadId: activeDetail.lead.id,
        customerId: activeConversionDraft.customerId || null,
        customerName: activeConversionDraft.customerName.trim() || undefined,
        projectName: activeConversionDraft.projectName.trim() || undefined,
        primaryDomain: activeConversionDraft.primaryDomain.trim() || null
      });
      navigate(`/projects/${result.project.id}`);
    } catch (nextError) {
      setConversionError(nextError instanceof Error ? nextError.message : 'Lead conversion failed.');
    } finally {
      setIsConverting(false);
    }
  }

  return (
    <EditorPageLayout
      hero={
        <EditorHeroCard
          actions={
            <>
              <button className="av-secondary-button" type="button" onClick={() => void handleDeleteLead()}>
                <Icon name="trash" />
                <span>{t('shell.delete')}</span>
              </button>
              <OverflowMenuButton />
            </>
          }
        >
          <div className="av-editor-hero-card__content">
            <div className="av-editor-hero-card__identity">
              <div className="av-editor-hero-card__icon">
                <Icon name="leads" />
              </div>
              <div className="av-editor-hero-card__identity-copy">
                <h2>{model.title}</h2>
                <div className="av-editor-hero-card__meta">
                  <StatusBadge label={model.statusLabel} tone={model.statusTone} />
                  <StatusBadge label={model.nextContactLabel} tone="primary" />
                  <span className="av-category-chip">
                    <Icon name={model.categoryIconName} />
                    <span>{model.categoryLabel}</span>
                    {model.categoryArchived ? <em>{t('common.archived')}</em> : null}
                  </span>
                </div>
                <span>{model.subtitle}</span>
              </div>
            </div>

            <div className="av-editor-hero-card__endpoints">
              {model.phone ? (
                <button className="av-inline-link av-inline-link-button" type="button" onClick={() => void openExternal(toTelUrl(model.phone!))}>
                  <Icon name="phone" />
                  <span>{model.phone}</span>
                </button>
              ) : null}
              {model.website ? (
                <button className="av-inline-link av-inline-link-button" type="button" onClick={() => void openExternal(model.website!)}>
                  <Icon name="website" />
                  <span>{model.website}</span>
                </button>
              ) : null}
            </div>
          </div>
        </EditorHeroCard>
      }
      heroAside={
        <EditorSectionCard title={t('leads.editor.panels.nextAction')} actions={<OverflowMenuButton />}>
          <div className="av-summary-stack">
            <ValuePair label={t('leads.editor.fields.nextContactAt')} value={model.nextContactLabel} />
            <ValuePair label={t('leads.editor.panels.lastActivity')} value={model.lastActivityLabel} />
            <button className="av-primary-button av-secondary-button--full" type="button" onClick={() => setEditorTab('contact')}>
              <Icon name="phone" />
              <span>{t('leads.editor.actions.logContact')}</span>
            </button>
          </div>
        </EditorSectionCard>
      }
      tabs={
        <EditorTabs
          tabs={[
            { id: 'general', label: t('leads.editor.tabs.general'), icon: 'leads' },
            { id: 'contact', label: t('leads.editor.tabs.contact'), icon: 'phone' },
            { id: 'conversion', label: t('leads.editor.tabs.conversion'), icon: 'projects' }
          ]}
          value={editorTab}
          onChange={setEditorTab}
        />
      }
    >
      <EditorBodyLayout
        main={
          <>
            {saveError ? <div className="av-inline-error">{saveError}</div> : null}
            {editorTab === 'general' ? (
              <EditorSectionCard
                title={t('leads.editor.sections.general')}
                actions={
                  <div className="av-inline-metadata">
                    <button className="av-secondary-button" type="button" onClick={handleCancel} disabled={!isDirty || isSaving}>
                      {t('common.cancel')}
                    </button>
                    <button className="av-primary-button" type="button" onClick={() => void handleSave()} disabled={!isDirty || isSaving}>
                      {t('common.applyChanges')}
                    </button>
                  </div>
                }
              >
                <div className="av-form-grid">
                  <label className="av-form-field">
                    <span>{t('leads.editor.fields.name')}</span>
                    <TextField value={draft.name} onValueChange={(value) => setDraft((current) => (current ? { ...current, name: value } : current))} />
                  </label>
                  <label className="av-form-field">
                    <span>{t('leads.editor.fields.status')}</span>
                    <LeadStatusControl
                      leadName={draft.name}
                      t={t}
                      value={draft.status}
                      onChange={(status) => setDraft((current) => (current ? { ...current, status } : current))}
                    />
                  </label>
                  <label className="av-form-field">
                    <span>{t('leads.editor.fields.customerCategory')}</span>
                    <SearchableSelectField
                      clearLabel={t('common.clear')}
                      emptyLabel={t('common.noResults')}
                      options={customerCategoryOptions}
                      placeholder={t('leads.filters.categoryPlaceholder')}
                      searchPlaceholder={t('leads.filters.searchCategory')}
                      value={draft.customerCategoryId}
                      onValueChange={(value) => setDraft((current) => (current ? { ...current, customerCategoryId: value } : current))}
                    />
                  </label>
                  <label className="av-form-field">
                    <span>{t('leads.editor.fields.companyName')}</span>
                    <TextField
                      value={draft.companyName}
                      onValueChange={(value) => setDraft((current) => (current ? { ...current, companyName: value } : current))}
                    />
                  </label>
                  <label className="av-form-field">
                    <span>{t('leads.editor.fields.contactName')}</span>
                    <TextField
                      value={draft.contactName}
                      onValueChange={(value) => setDraft((current) => (current ? { ...current, contactName: value } : current))}
                    />
                  </label>
                  <label className="av-form-field">
                    <span>{t('leads.editor.fields.phone')}</span>
                    <TextField value={draft.phone} onValueChange={(value) => setDraft((current) => (current ? { ...current, phone: value } : current))} />
                  </label>
                  <label className="av-form-field">
                    <span>{t('leads.editor.fields.email')}</span>
                    <TextField value={draft.email} onValueChange={(value) => setDraft((current) => (current ? { ...current, email: value } : current))} />
                  </label>
                  <label className="av-form-field">
                    <span>{t('leads.editor.fields.currentWebsiteUrl')}</span>
                    <TextField
                      value={draft.currentWebsiteUrl}
                      onValueChange={(value) => setDraft((current) => (current ? { ...current, currentWebsiteUrl: value } : current))}
                    />
                  </label>
                  <label className="av-form-field">
                    <span>{t('leads.editor.fields.city')}</span>
                    <TextField value={draft.city} onValueChange={(value) => setDraft((current) => (current ? { ...current, city: value } : current))} />
                  </label>
                  <label className="av-form-field">
                    <span>{t('leads.editor.fields.source')}</span>
                    <TextField value={draft.source} onValueChange={(value) => setDraft((current) => (current ? { ...current, source: value } : current))} />
                  </label>
                  <label className="av-form-field">
                    <span>{t('leads.editor.fields.nextContactAt')}</span>
                    <TextField
                      type="date"
                      value={draft.nextContactAt}
                      onValueChange={(value) => setDraft((current) => (current ? { ...current, nextContactAt: value } : current))}
                    />
                  </label>
                  <label className="av-form-field">
                    <span>{t('leads.editor.fields.tags')}</span>
                    <TextField value={draft.tags} onValueChange={(value) => setDraft((current) => (current ? { ...current, tags: value } : current))} />
                  </label>
                </div>
                <label className="av-form-field">
                  <span>{t('leads.editor.fields.lostReason')}</span>
                  <TextAreaField
                    rows={3}
                    value={draft.lostReason}
                    onValueChange={(value) => setDraft((current) => (current ? { ...current, lostReason: value } : current))}
                  />
                </label>
              </EditorSectionCard>
            ) : null}

            {editorTab === 'contact' ? (
              <>
                <EditorSectionCard title={t('leads.editor.sections.logActivity')} actions={<OverflowMenuButton />}>
                  {activityError ? <div className="av-inline-error">{activityError}</div> : null}
                  <div className="av-form-grid">
                    <label className="av-form-field">
                      <span>{t('leads.editor.fields.activityKind')}</span>
                      <SelectField
                        value={activityDraft.kind}
                        options={activityKindOptions}
                        onChange={(event) => setActivityDraft((current) => ({ ...current, kind: event.target.value as LeadActivityKind }))}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('leads.editor.fields.activityOutcome')}</span>
                      <SelectField
                        value={activityDraft.outcome}
                        options={activityOutcomeOptions}
                        onChange={(event) => setActivityDraft((current) => ({ ...current, outcome: event.target.value as LeadActivityOutcome }))}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('leads.editor.fields.nextContactAt')}</span>
                      <TextField
                        type="date"
                        value={activityDraft.nextContactAt}
                        onValueChange={(value) => setActivityDraft((current) => ({ ...current, nextContactAt: value }))}
                      />
                    </label>
                    <label className="av-form-field">
                      <span>{t('leads.editor.fields.status')}</span>
                      <SelectField
                        value={activityDraft.status}
                        options={[{ value: '', label: t('common.leaveUnchanged') }, ...statusOptions]}
                        onChange={(event) => setActivityDraft((current) => ({ ...current, status: event.target.value as '' | LeadStatus }))}
                      />
                    </label>
                  </div>
                  <label className="av-form-field">
                    <span>{t('leads.editor.fields.comment')}</span>
                    <TextAreaField
                      rows={4}
                      value={activityDraft.comment}
                      onValueChange={(value) => setActivityDraft((current) => ({ ...current, comment: value }))}
                    />
                  </label>
                  <button className="av-primary-button" type="button" onClick={() => void handleLogActivity()}>
                    <Icon name="addNote" />
                    <span>{t('leads.editor.actions.saveActivity')}</span>
                  </button>
                </EditorSectionCard>

                <EditorSectionCard title={t('leads.editor.sections.activityTimeline')} actions={<OverflowMenuButton />}>
                  {model.activities.length === 0 ? (
                    <EmptyState title={t('leads.editor.sections.activityTimeline')} description={t('leads.editor.empty.activities')} />
                  ) : (
                    <ul className="av-compact-list">
                      {model.activities.map((activity) => (
                        <li key={activity.id} className="av-compact-list__item">
                          <div>
                            <strong>{activity.title}</strong>
                            <span>{activity.meta}</span>
                          </div>
                          {activity.comment ? <p>{activity.comment}</p> : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </EditorSectionCard>
              </>
            ) : null}

            {editorTab === 'conversion' ? (
              <EditorSectionCard title={t('leads.editor.sections.conversion')} actions={<OverflowMenuButton />}>
                {conversionError ? <div className="av-inline-error">{conversionError}</div> : null}
                {isConverted ? (
                  <div className="av-summary-stack">
                    <StatusBadge label={t('enums.leadStatus.converted')} tone="success" />
                    {detail.lead.customerId ? (
                      <Link className="av-inline-link" to={`/customers/${detail.lead.customerId}`}>
                        {model.customerName ?? t('navigation.customers')}
                        <Icon name="chevron" />
                      </Link>
                    ) : null}
                    {detail.lead.projectId ? (
                      <Link className="av-inline-link" to={`/projects/${detail.lead.projectId}`}>
                        {model.projectName ?? t('navigation.projects')}
                        <Icon name="chevron" />
                      </Link>
                    ) : null}
                  </div>
                ) : (
                  <>
                    <div className="av-form-grid">
                      <label className="av-form-field">
                        <span>{t('leads.conversion.customer')}</span>
                        <SelectField
                          value={conversionDraft.customerId}
                          options={customerOptions}
                          onChange={(event) => setConversionDraft((current) => (current ? { ...current, customerId: event.target.value } : current))}
                        />
                      </label>
                      <label className="av-form-field">
                        <span>{t('leads.conversion.customerName')}</span>
                        <TextField
                          value={conversionDraft.customerName}
                          onValueChange={(value) => setConversionDraft((current) => (current ? { ...current, customerName: value } : current))}
                        />
                      </label>
                      <label className="av-form-field">
                        <span>{t('leads.conversion.projectName')}</span>
                        <TextField
                          value={conversionDraft.projectName}
                          onValueChange={(value) => setConversionDraft((current) => (current ? { ...current, projectName: value } : current))}
                        />
                      </label>
                      <label className="av-form-field">
                        <span>{t('leads.conversion.primaryDomain')}</span>
                        <TextField
                          value={conversionDraft.primaryDomain}
                          onValueChange={(value) => setConversionDraft((current) => (current ? { ...current, primaryDomain: value } : current))}
                        />
                      </label>
                    </div>
                    <button className="av-primary-button" type="button" disabled={isConverting} onClick={() => void handleConvertLead()}>
                      <Icon name="projects" />
                      <span>{t('leads.editor.actions.convert')}</span>
                    </button>
                  </>
                )}
              </EditorSectionCard>
            ) : null}
          </>
        }
        rail={
          <>
            <EditorSectionCard title={t('leads.editor.panels.contact')} actions={<OverflowMenuButton />}>
              <div className="av-summary-stack">
                <ValuePair label={t('leads.editor.fields.phone')} value={model.phone ?? t('common.notSet')} />
                <ValuePair label={t('leads.editor.fields.email')} value={model.email ?? t('common.notSet')} />
                <ValuePair label={t('leads.editor.fields.city')} value={activeDetail.lead.city ?? t('common.notSet')} />
              </div>
            </EditorSectionCard>

            <EditorSectionCard title={t('leads.editor.panels.conversion')} actions={<OverflowMenuButton />}>
              <div className="av-summary-stack">
                <ValuePair label={t('navigation.customers')} value={model.customerName ?? t('common.notSet')} />
                <ValuePair label={t('navigation.projects')} value={model.projectName ?? t('common.notSet')} />
                <button className="av-secondary-button av-secondary-button--full" type="button" onClick={() => setEditorTab('conversion')}>
                  <Icon name="projects" />
                  <span>{isConverted ? t('leads.editor.actions.viewConversion') : t('leads.editor.actions.convert')}</span>
                </button>
              </div>
            </EditorSectionCard>
          </>
        }
      />
    </EditorPageLayout>
  );
}
